import { useEffect, useState } from "react";
import useAppSelector from "./redux/base/useAppSelector";

const useStepSpecificValidation = (fullSchema, formalSchema, methods, onSubmit) => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<any>(null);

  const autoValidation = useAppSelector((state) => {
    return state.AutoServiceValidation?.autoValidation;
  });

  useEffect(() => {
    if (!autoValidation) return;

    const runValidation = async () => {
      try {
        await fullSchema().validate(methods.getValues(), { abortEarly: false, context: methods.getValues() });
      } catch (err: any) {
        const yupErrors = err.inner ?? [];

        setTimeout(() => {
          yupErrors.forEach((error: any) => {
            if (error.path) {
              methods.setError(error.path, {
                type: "manual",
                message: error.message,
              });
            }
          });
        }, 0);
      }
    };

    runValidation().then(() => {
      // This is necessary because the input fields only display errors after the form has been submitted at least once,
      // as controlled by the `shouldShowFieldError` logic in nacid-components.
      methods.handleSubmit(() => {})();
    });
  }, [autoValidation]);

  const handleCancel = async () => {
    if (!formData) return;
    try {
      await fullSchema().validate(formData, { abortEarly: false, context: formData });
    } catch (err) {
      const yupErrors = err.inner ?? [];
      yupErrors.forEach((error: any) => {
        if (error.path) {
          methods.setError(error.path, {
            type: "manual",
            message: error.message,
          });
        }
      });
    } finally {
      setFormData(null);
      setShowModal(false);
    }
  };

  const handleAccept = () => {
    if (!formData) return;
    onSubmit(formData);
    setFormData(null);
    setShowModal(false);
  };

  const onSubmitWithValidations = async (data: any) => {
    try {
      await formalSchema().validate(data, { abortEarly: false, context: data });

      try {
        await fullSchema().validate(data, {
          abortEarly: false,
          context: data,
        });
        onSubmit(data);
      } catch (fullError) {
        setShowModal(true);
        setFormData(data);
      }
    } catch (formalError) {
      const yupErrors = formalError.inner ?? [];
      yupErrors.forEach((error: any) => {
        if (error.path) {
          methods.setError(error.path, {
            type: "manual",
            message: error.message,
          });
        }
      });
    }
  };

  return {
    onSubmitWithValidations,
    showModal,
    handleCancel,
    handleAccept,
  };
};

export default useStepSpecificValidation;
